class PredictiveSearch extends HTMLElement {
	constructor() {
		super()
		this.result = null
		this.abortController = null
		this.predictiveUrl = window.routes.predictive_search_url
		this.domParser = new DOMParser()

		// Bind methods to preserve context
		this.handleInputChange = this.handleInputChange.bind(this)
		this.handleClickReset = this.handleClickReset.bind(this)
		this.handleClickTerm = this.handleClickTerm.bind(this)
		this.getSearchResults = this.getSearchResults.bind(this)

		this.searchResults = this.querySelector('[data-predictive-search-results]')
		this.defaultContent = this.querySelector('[data-predictive-search-default-content]')

		this.isOpen = false
	}

	connectedCallback() {
		this.setupEventListeners()
		this.#setupStories()
	}

	disconnectedCallback() {
		this.removeEventListeners()
		if (this.abortController) {
			this.abortController.abort('Component disconnected')
		}
	}

	#setupStories() {
		const stories = this.querySelector('.orsay-stories')
		if (!stories) return

		this.querySelectorAll('.predictive-search__story')?.forEach((story, index) => {
			const video = story.querySelector('video')
			if (!video) return
			story.addEventListener('mouseenter', () => {
				video.play()
			})
			story.addEventListener('mouseleave', () => {
				video.pause()
				video.currentTime = 0
			})
			story.addEventListener('click', event => {
				stories.querySelector('.orsay-stories__feed-container').style.transformOrigin = `${event.clientX}px ${event.clientY}px`
				stories.goToStory(index)
				stories.show()
			})
		})
	}

	open() {
		this.isOpen = true
		this.setAttribute('open', true)
		document.body.style.overflow = 'hidden'
	}

	close() {
		this.isOpen = false
		this.removeAttribute('open')
		document.body.style.overflow = 'auto'
	}

	setupEventListeners() {
		const input = this.querySelector('[data-predictive-search-input]')
		const resetButton = this.querySelector('[data-predictive-search-reset]')
		const suggestions = this.querySelector('[data-predictive-search-suggestions]')

		if (input) {
			input.addEventListener('input', this.handleInputChange)
		}

		if (resetButton) {
			resetButton.addEventListener('click', this.handleClickReset)
		}

		// Add event listeners for suggestion terms
		this.addEventListener('click', e => {
			if (e.target.matches('[data-predictive-search-term]')) {
				this.handleClickTerm(e)
			}
		})
	}

	removeEventListeners() {
		const input = this.querySelector('[data-predictive-search-input]')
		const resetButton = this.querySelector('[data-predictive-search-reset]')

		if (input) {
			input.removeEventListener('input', this.handleInputChange)
		}

		if (resetButton) {
			resetButton.removeEventListener('click', this.handleClickReset)
		}
	}

	getSearchResults(searchTerm) {
		if (this.abortController) {
			this.abortController.abort('Fetch cancelled')
		}

		if (searchTerm.length > 1) {
			this.abortController = new AbortController()

			this.result = fetch(`${this.predictiveUrl}?q=${searchTerm}&section_id=predictive-search-results&resources[limit]=20`, {
				signal: this.abortController.signal
			})
				.then(response => {
					if (!response.ok) {
						const error = new Error(response.status)
						throw error
					}
					return response.text()
				})
				.then(text => {
					const result = this.domParser.parseFromString(text, 'text/html')
					const searchResults = result?.getElementById('predictive-search-results')
					console.log('searchResults', searchResults)
					const predictiveSearchResults = this.querySelector('[data-predictive-search-results]')
					const suggestedSearchChildren = [...predictiveSearchResults.children]

					if (searchResults) {
						predictiveSearchResults.replaceChildren(...searchResults.children)

						this.searchResults.style.display = 'block'

						if (this.defaultContent) {
							this.defaultContent.style.display = 'none'
						}
					} else {
						if (result) {
							predictiveSearchResults.replaceChildren(...result.children)
						}

						this.searchResults.style.display = 'none'

						if (this.defaultContent && this.defaultContent.style.display !== '') {
							this.defaultContent.style.display = ''
						}
					}
				})
				.catch(error => {
					console.error('Predictive search error:', error)
				})
				.finally(() => {
					this.abortController = null
				})
		} else {
			this.reset()
		}
	}

	reset() {
		const predictiveSearchResults = this.querySelector('[data-predictive-search-results]')
		const suggestedSearchChildren = [...predictiveSearchResults.children]

		this.searchResults.style.display = 'none'

		if (this.defaultContent) {
			this.defaultContent.style.display = ''
		}

		predictiveSearchResults.replaceChildren(...suggestedSearchChildren)
	}

	handleInputChange(e) {
		const value = e.target.value
		this.getSearchResults(value)
	}

	handleClickReset() {
		if (this.abortController) {
			this.abortController.abort('Fetch cancelled')
		}

		const input = this.querySelector('[data-predictive-search-input]')
		if (input) {
			input.value = ''
			requestAnimationFrame(() => {
				input.focus()
			})
		}

		this.reset()
	}

	handleClickTerm(e) {
		const value = e.target.textContent.trim()
		const input = this.querySelector('[data-predictive-search-input]')
		if (input) {
			input.value = value
		}
		this.getSearchResults(value)
	}
}

// Register the custom element
customElements.define('predictive-search', PredictiveSearch)
